<script setup lang="ts">

import {computed, onMounted, Ref, ref} from "vue";
import pulseBackend from "@/backend/pulse.backend";
import WeekPicker from "@/components/WeekPicker.vue";
import {DateTime} from "luxon";
import {useRoute, useRouter} from "vue-router";
import Debug from "@/components/Debug.vue";
import PulseWeekStats from "@/components/pulse/PulseWeekStats.vue";
import {CompanyPulseData} from "@/types/DataTypes.ts";
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import TeamWeekAllocationSummary from "./TeamWeekAllocationSummary.vue";
import ExpandablePersonEntry from "./ExpandablePersonEntry.vue";
import CompanyWeekAllocationBar from "./CompanyWeekAllocationBar.vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import PulseGroupingControls from "./PulseGroupingControls.vue";

const router = useRouter();
const route = useRoute();

const loading = ref(false)
const weekStart: Ref<DateTime> = ref<DateTime>(initWeek())
const pulses = ref<CompanyPulseData[] | null>()
const filter = ref()
const statsKey = ref(0)
const groupBy = ref<'team' | 'function'>('team')

const filtered = computed(() => {
  if (!pulses.value) return null
  return pulses.value.filter((p) => {
    if (!filter.value) return true
    const term = filter.value.toLowerCase()
    return p.user.name.toLowerCase().includes(term) ||
      (p.user.teamObj?.name && p.user.teamObj.name.toLowerCase().includes(term)) ||
      (p.user.department?.name && p.user.department.name.toLowerCase().includes(term))
  })
})

const teams = computed(() => {
  if (!filtered.value) return null
  return [...new Set(filtered.value.map((p) => p.user.teamObj?.name))].sort((a, b) => a ? a.localeCompare(b) : 1)
});

const functions = computed(() => {
  if (!filtered.value) return null
  return [...new Set(filtered.value.map((p) => p.user.department?.name || 'Unassigned'))].sort((a, b) => a.localeCompare(b))
});

const groups = computed(() => {
  return groupBy.value === 'team' ? teams.value : functions.value;
});

const groupLabel = computed(() => {
  return groupBy.value === 'team' ? 'Team' : 'Function';
});

function initWeek() {
  const baseDate = route.query.week ? DateTime.fromISO(route.query.week as string) : DateTime.now()
  return baseDate.startOf('day').startOf('week')
}

async function loadPulses() {
  loading.value = true
  const isoDate = weekStart.value.toISODate();
  pulses.value = await pulseBackend.week(isoDate!)();
  statsKey.value++;
  loading.value = false
}


onMounted(async () => {
  const weekOf = route.query.week ? DateTime.fromISO(route.query.week as string) : DateTime.now().startOf('day').startOf('week')
  if (weekOf.toISODate() !== weekStart.value.toISODate()) {
    weekStart.value = weekOf
    pulses.value = null
  }

  if (!pulses.value) {
    await loadPulses()
  }
})

async function switchWeek() {
  await router.push({query: {week: weekStart.value.toISODate()}})
  await loadPulses();
}

function filterByTeam(team: string) {
  return filtered.value?.filter((p) => p.user.teamObj?.name === team)
}

function filterByGroup(groupName: string) {
  if (groupBy.value === 'team') {
    return filterByTeam(groupName);
  } else {
    // Filter by function (department)
    return filtered.value?.filter((p) => (p.user.department?.name || 'Unassigned') === groupName)
  }
}

</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <WeekPicker v-model="weekStart" @change="switchWeek"/>
      <PulseGroupingControls v-model="groupBy" />
      <IconField>
        <InputIcon><i class="pi pi-search"/></InputIcon>
        <InputText v-model="filter" placeholder="Filter Teams, Functions & Names"/>
      </IconField>
    </div>

    <!-- Company-wide allocation bar -->
    <div v-if="pulses" class="border border-surface-200 dark:border-surface-700 rounded-lg p-4 mb-4  bg-surface">
      <div class="flex items-center gap-4">
        <div class="flex-grow">
          <CompanyWeekAllocationBar :pulses="pulses" />
        </div>
      </div>
    </div>

    <Accordion :value="groups" multiple>
      <AccordionPanel v-for="group in groups" :value="group">
        <AccordionHeader>
          <div class="flex items-center w-full pr-4 gap-4 justify-between">
            <span class="w-1/4">{{ group }}</span>
            <TeamWeekAllocationSummary :team-pulses="filterByGroup(group) || []" class="w-1/2"/>
          </div>
        </AccordionHeader>
        <AccordionContent>
          <div class="flex flex-col gap-2">
            <ExpandablePersonEntry
              v-for="pulse in filterByGroup(group)"
              :key="pulse.user.id"
              :pulse="pulse"
            />
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
    <Debug :data="pulses"/>
  </div>
</template>
